import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { useResellerFromSubdomain } from '../../../models/hooks/useResellerFromSubdomain';
import type { AdminUserSessionContext } from '../../../viewmodels/AdminViewModels';
import { LoginViewModel, UserSessionContext } from '../../../viewmodels/AppViewModels';
import googleLogo from '../../assets/google_g.png';
import microsoftLogo from '../../assets/microsoft-logo.png';
import { DeprecatedPopover, PopoverType } from '../../components/DeprecatedPopover';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { CityLeftGraphic } from '../../components/svgs/graphics/CityLeftGraphic';
import { CityRightGraphic } from '../../components/svgs/graphics/CityRightGraphic';
import { MoonAndStarsGraphic } from '../../components/svgs/graphics/MoonAndStarsGraphic';
import { ResellerLogoIcon } from '../../components/svgs/icons/ResellerLogoIcon';
import { baseStyleSheet } from '../../styles/styles';
import { ILoginContext, LoginContext } from './context';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { getAdminLandingPath } from './getAdminLandingPath';
import { FormFieldErrorMessagePopover, Login2FAFields, LoginEmailField, LoginPasswordField } from './presentation';
import { styleSheet } from './styles';

export interface ILoginProps {
	autoRedirectIfAuthenticated?: boolean;
	className?: string;
	colorScheme?: string;
	loginMode?: 'standard' | 'admin';
	loginVM?: LoginViewModel;
	showRememberMe?: boolean;
	showSignupButton?: boolean;
	userSession?: UserSessionContext;
	reseller?: Api.Reseller;
}

type LoginMethod = 'password' | 'oauth';
enum OAuthProvider {
	GoogleOAuth = 'GoogleOAuth',
	MicrosoftOAuth = 'MicrosoftOAuth',
}

interface IState {
	redirect?: boolean;
	showForgotPasswordDialog?: boolean;
	loginVM?: LoginViewModel;
	availableLoginMethods?: LoginMethod[];
	availableOAuthProviders?: OAuthProvider[];
	isCheckingLoginMethods?: boolean;
	showLoginFields?: boolean;
}

class _Login extends React.Component<
	ILoginProps & RouteComponentProps<any, any, { from?: Location; search?: string }>,
	IState
> {
	private mLoginContext: ILoginContext;
	public static defaultProps: ILoginProps = {
		autoRedirectIfAuthenticated: true,
		loginMode: 'standard',
		showRememberMe: true,
		showSignupButton: true,
	};

	constructor(props: ILoginProps & RouteComponentProps<any, any, { from?: Location; search?: string }>) {
		super(props);

		makeObservable<_Login, 'showingTwoFA'>(this, {
			showingTwoFA: computed,
		});

		this.mLoginContext = {
			loginViewModel: props.loginVM || this.getDefaultViewModel(),
		};

		this.state = {
			loginVM: this.mLoginContext.loginViewModel,
			redirect: !!props.autoRedirectIfAuthenticated && !!props.userSession.isAuthenticated,
			showForgotPasswordDialog: false,
			availableLoginMethods: undefined,
			availableOAuthProviders: undefined,
			isCheckingLoginMethods: false,
			showLoginFields: false,
		};
	}

	private getDefaultViewModel() {
		return new LoginViewModel(this.props.userSession, () => this.setState({ redirect: true }));
	}

	private get showingTwoFA() {
		const { loginVM } = this.state;
		return loginVM?.needsPhone || loginVM?.challenging;
	}

	public render() {
		const { userSession, showSignupButton, reseller, loginMode } = this.props;
		const {
			redirect,
			loginVM,
			showForgotPasswordDialog,
			showLoginFields,
			availableLoginMethods,
			availableOAuthProviders,
			isCheckingLoginMethods,
		} = this.state;

		if (!!redirect && userSession.isAuthenticated) {
			// Permissions load async after session load; deciding the admin landing page on an
			// incomplete map would send a full admin to /support before their permissions arrive.
			if (loginMode === 'admin' && (userSession as AdminUserSessionContext)?.isLoadingPermissions) {
				return null;
			}
			return this.renderRedirect();
		}

		const showPasswordField = showLoginFields && availableLoginMethods?.includes('password');
		const showOAuthButtons = showLoginFields && availableLoginMethods?.includes('oauth');
		const showGoogleButton =
			showOAuthButtons && availableOAuthProviders?.includes(OAuthProvider.GoogleOAuth.toLowerCase());
		const showMicrosoftButton =
			showOAuthButtons && availableOAuthProviders?.includes(OAuthProvider.MicrosoftOAuth.toLowerCase());
		const isBusy = loginVM?.isBusy || isCheckingLoginMethods;
		const signupButtonText = this.isMobileClient() ? 'Learn More' : "Don't have an account? Sign Up";

		return (
			<LoginContext.Provider value={this.mLoginContext}>
				<div className={css(styleSheet.standardContainer)}>
					<div className={css(styleSheet.flexContainer)}>
						<div>
							{!isBusy && <ResellerLogoIcon className={css(styleSheet.logo)} reseller={reseller} />}
							{isBusy ? (
								<LoadingSpinner type='login' className={css(styleSheet.loader)} />
							) : (
								<div className={css(styleSheet.login)}>
									<div className={css(styleSheet.graphicContainer)}>
										<MoonAndStarsGraphic className={css(styleSheet.moonAndStars)} />
										<CityLeftGraphic className={css(styleSheet.cityLeft)} />
										<CityRightGraphic className={css(styleSheet.cityRight)} />
										<div className={css(styleSheet.containerBottom)} />
									</div>
									<div className={css(styleSheet.loginForm)}>
										<FormFieldErrorMessagePopover fieldName='email'>
											<LoginEmailField
												disabled={showLoginFields}
												onBackClick={showLoginFields && !this.showingTwoFA ? this.onBackButtonClick : undefined}
												onKeyDown={!showLoginFields && !this.showingTwoFA ? this.onEmailFieldKeyDown : undefined}
											/>
										</FormFieldErrorMessagePopover>
										{showPasswordField && (
											<FormFieldErrorMessagePopover fieldName='password'>
												<LoginPasswordField onKeyDown={!this.showingTwoFA ? this.onLastFieldKeyDown : undefined} />
											</FormFieldErrorMessagePopover>
										)}
										<Login2FAFields />
										{!showLoginFields && !this.showingTwoFA ? (
											<button className={css(styleSheet.submitButton)} onClick={this.onNextButtonClick}>
												Next
											</button>
										) : (
											showPasswordField && (
												<DeprecatedPopover
													anchor={
														<button className={css(styleSheet.submitButton)} onClick={this.onSubmitSignInForm}>
															{loginVM?.needsPhone ? 'Next' : loginVM?.challenging ? 'Verify' : 'Sign In'}
														</button>
													}
													isOpen={loginVM?.hasErrorMessage('global') && !showForgotPasswordDialog}
													type={PopoverType.error}
													contentClassName='login-error-popover'
													preferredPlacement='below'
													dismissOnClickOutside={true}
													onRequestClose={() => loginVM?.clearError()}
												>
													<div style={{ maxWidth: 250 }}>
														<span>{loginVM?.getErrorMessage('global')}</span>
													</div>
												</DeprecatedPopover>
											)
										)}
										{!this.showingTwoFA && (
											<>
												{showGoogleButton && (
													<button
														type='button'
														className={css(styleSheet.oauthButton)}
														onClick={this.onContinueWithGoogle}
													>
														<img src={googleLogo} alt='Google' className={css(styleSheet.oauthLogo)} />
														Continue with Google
													</button>
												)}
												{showMicrosoftButton && (
													<button
														type='button'
														className={css(styleSheet.oauthButton)}
														onClick={this.onContinueWithMicrosoft}
													>
														<img src={microsoftLogo} alt='Microsoft' className={css(styleSheet.oauthLogo)} />
														Continue with Microsoft
													</button>
												)}
											</>
										)}
										<div className={css(styleSheet.footer)}>
											{!this.showingTwoFA && showPasswordField && (
												<span
													onClick={this.onSetForgotPasswordModalOpen(true)}
													className={css(styleSheet.forgotPasswordLink)}
												>
													Forgot Password
												</span>
											)}
										</div>
									</div>
									{!!showSignupButton && (
										<a className={css(styleSheet.signUpButton)} href='https://levitate.ai/#/pricing'>
											{signupButtonText}
										</a>
									)}
									<span className={css(styleSheet.googleMessage)}>
										{
											"For GSuite and Gmail Users: Levitate's use of information received from Google APIs will adhere to "
										}
										<a
											className={css(baseStyleSheet.brandSecondaryLink)}
											href='https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes'
											target='_blank'
											rel='noreferrer'
										>
											Google API Services User Data Policy
										</a>
										, including the Limited Use requirements.
									</span>
									<ForgotPasswordModal
										initialEmailAddress={loginVM?.email}
										modalProps={{
											isOpen: showForgotPasswordDialog,
											onRequestClose: this.onSetForgotPasswordModalOpen(false),
										}}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</LoginContext.Provider>
		);
	}

	private renderRedirect() {
		const { location, loginMode, userSession } = this.props;
		let pathname = '/dashboard';
		let search: string;
		if (loginMode === 'admin') {
			// Permissions have finished loading by here (see the isLoadingPermissions guard above).
			pathname = getAdminLandingPath(userSession as AdminUserSessionContext);
			if (Api.isProdEnvironment) {
				search = `planId=business-paid`;
			}
		}

		const fromLocation = location.state?.from;
		if (fromLocation) {
			pathname = fromLocation.pathname;
			if (fromLocation.search) {
				search = fromLocation.search;
			}
		}

		return (
			<Redirect
				to={{
					pathname,
					search: search || location.state?.search,
					state: { from: location },
				}}
			/>
		);
	}

	private onSetForgotPasswordModalOpen = (show: boolean) => () => {
		this.setState({ showForgotPasswordDialog: show });
	};

	private checkLoginMethods = async () => {
		const { loginVM } = this.state;
		const email = loginVM?.email;

		if (!email) {
			if (loginVM) {
				loginVM.validationErrorField = 'email';
				loginVM.validationErrorMessage = 'Please enter your email address';
			}
			return;
		}

		this.setState({ isCheckingLoginMethods: true });

		try {
			const { userSession } = this.props;
			const baseUrl = userSession.webServiceHelper.baseUrl;
			const resource = `acct:${encodeURIComponent(email)}`;
			const url = `${baseUrl}/.well-known/webfinger?resource=${resource}`;

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					Accept: 'application/jrd+json',
				},
			});

			if (response.ok) {
				const data = await response.json();
				const loginMethodsString = data.properties?.['login-methods'] || '';
				const methods = loginMethodsString.split(',').filter((m: string) => m) as LoginMethod[];

				const oauthProvidersString = data.properties?.['oauth-providers'] || '';
				const oauthProviders = oauthProvidersString.split(',').filter((p: string) => p) as OAuthProvider[];

				this.setState({
					availableLoginMethods: methods.length > 0 ? methods : ['password'],
					availableOAuthProviders: oauthProviders.length > 0 ? oauthProviders : undefined,
					showLoginFields: true,
					isCheckingLoginMethods: false,
				});
			} else {
				// Default to password on error
				this.setState({
					availableLoginMethods: ['password'],
					availableOAuthProviders: undefined,
					showLoginFields: true,
					isCheckingLoginMethods: false,
				});
			}
		} catch {
			// Default to password on error
			this.setState({
				availableLoginMethods: ['password'],
				availableOAuthProviders: undefined,
				showLoginFields: true,
				isCheckingLoginMethods: false,
			});
		}
	};

	private onNextButtonClick = () => {
		this.checkLoginMethods();
	};

	private onEmailFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.keyCode === 13 || e.key === 'Enter') {
			this.checkLoginMethods();
		}
	};

	private onBackButtonClick = () => {
		this.setState({
			showLoginFields: false,
			availableLoginMethods: undefined,
			availableOAuthProviders: undefined,
		});
	};

	/** Handle the event where the user hits enter in while editing the password field. */
	private onLastFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.keyCode === 13 || e.key === 'Enter') {
			this.onSubmitSignInForm();
		}
	};

	private onSubmitSignInForm = () => {
		this.state.loginVM.submit();
	};

	private isMobileClient = () => {
		const userAgent = window.navigator.userAgent;
		return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
	};

	private getExternalAuthIdParam = () => {
		const fromPath = this.props.location.state?.from?.pathname;
		const externalAuthIdMatch = fromPath?.match(/\/oauth\/external\/(.+)/);
		return externalAuthIdMatch ? `&externalAuthId=${encodeURIComponent(externalAuthIdMatch[1])}` : '';
	};

	private onContinueWithGoogle = () => {
		const { userSession } = this.props;
		const { loginVM } = this.state;
		const baseUrl = userSession.webServiceHelper.baseUrl;
		const params = new URLSearchParams(this.props.location.search);
		const username = loginVM?.email ? `&username=${encodeURIComponent(loginVM.email)}` : '';
		// Construct redirect_uri that preserves the full path (e.g., /admin.html for admin test environments)
		const currentBaseUrl = window.location.href.split('#')[0]; // Everything before the hash
		const redirectUri = params.get('redirect_uri') || `${currentBaseUrl}#/applinks/launch/auth/oauth-callback`;
		const clientId = params.get('client_id') ? `&clientId=${encodeURIComponent(params.get('client_id')!)}` : '';
		const state = params.get('state') ? `&state=${encodeURIComponent(params.get('state')!)}` : '';
		const externalAuthId = this.getExternalAuthIdParam();
		window.location.href = `${baseUrl}/user/authorization_url?provider=${OAuthProvider.GoogleOAuth}&redirectUri=${encodeURIComponent(redirectUri)}${username}${clientId}${state}${externalAuthId}`;
	};

	private onContinueWithMicrosoft = () => {
		const { userSession } = this.props;
		const { loginVM } = this.state;
		const baseUrl = userSession.webServiceHelper.baseUrl;
		const params = new URLSearchParams(this.props.location.search);
		const username = loginVM?.email ? `&username=${encodeURIComponent(loginVM.email)}` : '';
		// Construct redirect_uri that preserves the full path (e.g., /admin.html for admin test environments)
		const currentBaseUrl = window.location.href.split('#')[0]; // Everything before the hash
		const redirectUri = params.get('redirect_uri') || `${currentBaseUrl}#/applinks/launch/auth/oauth-callback`;
		const clientId = params.get('client_id') ? `&clientId=${encodeURIComponent(params.get('client_id')!)}` : '';
		const externalAuthId = this.getExternalAuthIdParam();
		window.location.href = `${baseUrl}/user/authorization_url?provider=${OAuthProvider.MicrosoftOAuth}&redirectUri=${encodeURIComponent(redirectUri)}${username}${clientId}${externalAuthId}`;
	};
}

const LoginWithRouter = withRouter(observer(_Login));

/**
 * Wrapper component that uses the subdomain-based reseller detection hook
 * and passes the reseller to the Login component for displaying the appropriate logo
 */
export const Login: React.FC<ILoginProps> = props => {
	const reseller = useResellerFromSubdomain();
	return <LoginWithRouter {...props} reseller={reseller} />;
};
